<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import InlineSvg from 'vue-inline-svg'
import { confettiBurst, createSleepingAnim } from './snowwhite-particles'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { useNavigation } from '../../useNavigation'

const asset = useStaticAsset()
const { currentUnitId } = useNavigation()
const props = defineProps<{ context: Student2Context }>()

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
const showAnimateBtn = ref(false)
const sleepAnimation = ref<{ stop: () => void } | null>(null)

const songs = computed(() => props.context.songs)

const getScene = () => {
  return document.querySelector('svg.student-v2-snowwhite__scene') as SVGSVGElement | null
}

async function animate() {
  showAnimateBtn.value = false
  sleepAnimation.value?.stop()
  const scene = getScene()
  if (!scene) return

  animateNotes()

  scene.querySelector('g#eyes-closed')?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 100, fill: 'forwards' })
  scene.querySelector('g#eyes-open')?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 100, fill: 'forwards' })

  await delay(1100)

  scene.querySelector('g#mouth-closed')?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 100, fill: 'forwards' })
  scene.querySelector('g#mouth-open')?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 100, fill: 'forwards' })

  scene.querySelector('g#arms-down')?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 100, fill: 'forwards' })
  scene.querySelector('g#arms-up')?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 100, fill: 'forwards' })

  const gown = scene.querySelector('#gown')
  if (!gown) return
  confettiBurst(gown, 5, { x: 160, y: 240 })

  for (let i = 0; i < 5; i++) {
    const keyframes = [{ transform: 'rotate(0deg)' }, { transform: 'rotate(40deg)' }]
    if (i % 2) keyframes.reverse()

    const animation = gown.animate(keyframes, { duration: 600, fill: 'forwards', easing: 'ease' })
    await animation.finished
    await delay(200)
  }
}

async function animateNotes() {
  const scene = getScene()
  if (!scene) return

  for (let i = 0; i < 3; i++) {
    for (const index in songs.value) {
      scene
        .querySelector(`[data-shape-for="${index}"]`)
        ?.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.05)' }, { transform: 'scale(1)' }], {
          duration: 500
        })
      scene
        .querySelector(`[data-text-for="${index}"]`)
        ?.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.05)' }, { transform: 'scale(1)' }], {
          duration: 500
        })

      await delay(300)
    }
  }
}

async function startIdleAnimation() {
  const scene = getScene()
  if (!scene) return

  sleepAnimation.value = createSleepingAnim(scene, { x: 50, y: 100 })

  await delay(1000)

  for (const i in songs.value) {
    const song = songs.value[i]

    const isLockedUnit = props.context.course_part.units //
      .some(e => e.id === song.unit_id && !e.unlocked)

    const isCurrentUnit = song.unit_id === currentUnitId.value
    const shape = scene.querySelector(`[data-shape-for="${i}"]`)

    if (!song.accepted) {
      if (isCurrentUnit) {
        shape?.setAttribute('stroke', '#f05b68')
        shape?.setAttribute('stroke-width', '2')
      } else if (isLockedUnit) shape?.setAttribute('fill', '#8c9ea3')
      continue
    }

    shape?.animate([{ fill: '#00CAFF' }, { fill: '#f05b68', stroke: 'white' }], { duration: 500, fill: 'forwards' })

    await delay(300)
  }

  showAnimateBtn.value = songs.value.every(s => s.accepted === true)
}

onMounted(() => {
  const checkSvg = async () => {
    const element = document.querySelector('svg.student-v2-snowwhite__scene')
    if (element) {
      startIdleAnimation()
      return
    }

    await nextTick()
    requestAnimationFrame(() => checkSvg())
  }

  checkSvg()
})
</script>

<template>
  <div class="student-v2-snowwhite">
    <transition name="fade">
      <v-button
        v-if="showAnimateBtn"
        mode="primary filled large"
        class="student-v2-snowwhite__animate-btn"
        @click="animate"
      >
        <v-icon class="mr-1">mdi-party-popper</v-icon>
        Помочь Белоснежке
      </v-button>
    </transition>

    <InlineSvg
      ref="scene"
      class="student-v2-snowwhite__scene"
      :src="asset('img/student-v2/staircase/snowwhite_scene.svg')"
      @load="startIdleAnimation"
    />
  </div>
</template>

<style lang="scss" scoped>
@function relPxVert($val) {
  @return calc((#{$val} / 780) * 100%);
}

@function relPxHoriz($val) {
  @return calc((#{$val} / 555) * 100%);
}

.student-v2-snowwhite {
  border-radius: 16px;
  border: 1px solid $lightGray;

  aspect-ratio: 555 / 780;
  max-width: min(555px, 90%);
  max-height: calc(100vh - var(--header_height) - 24px);

  position: relative;
  overflow: hidden;
  margin: 0 auto;

  &__animate-btn {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid white;
    border-radius: 8px;
  }
}
</style>
